import React, { useState, useMemo } from 'react';
import { useApp } from '../../context/AppContext';
import { useToast } from '../../context/ToastContext';
import { useConfirm } from '../../context/ConfirmContext';
import DataTable from '../common/DataTable';
import Modal from '../common/Modal';
import FormField, { ErrorSummary } from '../common/FormField';
import { Plus, Edit2, Trash2, Upload } from 'lucide-react';
import ImportModal from '../common/ImportModal';
import { formatCurrency } from '../../utils/helpers';
import { validators, validateForm, isAccountReferenced } from '../../utils/validation';

const emptyAccount = { name: '', group: '', groupId: '', type: 'Other', openingBalance: 0, balanceType: 'Dr' };

export default function AccountMaster() {
  const { state, dispatch } = useApp();
  const { addToast } = useToast();
  const confirm = useConfirm();
  const [showForm, setShowForm] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [form, setForm] = useState(emptyAccount);
  const [editing, setEditing] = useState(null);
  const [touched, setTouched] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const existingNames = useMemo(() =>
    state.accounts.map(a => ({ id: a.id, value: a.name })),
    [state.accounts]
  );

  const errors = useMemo(() => {
    const { errors } = validateForm({
      name: [
        validators.required(form.name, 'Account name'),
        validators.minLength(form.name, 2, 'Account name'),
        validators.maxLength(form.name, 100, 'Account name'),
        validators.unique(form.name, existingNames, 'Account name', editing),
      ],
      group: [
        validators.required(form.group, 'Account group'),
      ],
      openingBalance: [validators.positiveNumber(form.openingBalance, 'Opening balance')],
    });
    return errors;
  }, [form, existingNames, editing]);

  // Signed running balance the app uses (positive = Dr, negative = Cr).
  const signedBalance = () => (form.balanceType === 'Cr' ? -1 : 1) * (Number(form.openingBalance) || 0);

  const hasErrors = Object.values(errors).some(e => e);
  const touch = (field) => setTouched(prev => ({ ...prev, [field]: true }));

  const handleSave = () => {
    setSubmitted(true);
    setTouched({ name: true, group: true, openingBalance: true });
    if (hasErrors) return;
    // Derive Cash/Bank type from the selected Account Group name (keeps the
    // receipt/payment bank routing & cash/bank book working without a Type field).
    const grp = (state.accountGroups || []).find(g => g.id === form.groupId);
    const gtext = `${grp?.accountName || ''} ${grp?.groupName || ''}`.toLowerCase();
    const type = gtext.includes('bank') ? 'Bank' : gtext.includes('cash') ? 'Cash' : (form.type || 'Other');
    const payload = { ...form, type, openingBalance: Number(form.openingBalance) || 0, balance: signedBalance() };
    if (editing) {
      dispatch({ type: 'UPDATE_ACCOUNT', payload: { ...payload, id: editing } });
      addToast('Account updated successfully', 'success');
    } else {
      dispatch({ type: 'ADD_ACCOUNT', payload });
      addToast('Account created successfully', 'success');
    }
    closeForm();
  };

  const closeForm = () => {
    setShowForm(false);
    setForm(emptyAccount);
    setEditing(null);
    setTouched({});
    setSubmitted(false);
  };

  const handleEdit = (row) => {
    setForm({
      name: row.name,
      group: row.group || '',
      groupId: row.groupId || '',
      type: row.type || 'Other',
      openingBalance: Math.abs(Number(row.balance) || 0),
      balanceType: (Number(row.balance) || 0) < 0 ? 'Cr' : 'Dr',
    });
    setEditing(row.id);
    setTouched({});
    setSubmitted(false);
    setShowForm(true);
  };

  const handleDelete = async (row) => {
    const refs = isAccountReferenced(row, state);
    if (refs.length > 0) {
      alert(`Cannot delete: this account is referenced in ${refs.join(', ')}`);
      return;
    }
    if (row.balance !== 0) {
      alert(`Cannot delete: account has a non-zero balance (${formatCurrency(row.balance)})`);
      return;
    }
    const ok = await confirm('Delete this account? This action cannot be undone.', { title: 'Delete Account', variant: 'danger', confirmLabel: 'Delete' });
    if (ok) {
      dispatch({ type: 'DELETE_ACCOUNT', payload: row.id });
      addToast('Account deleted', 'success');
    }
  };

  const columns = [
    { key: 'name', label: 'Account Name', sortable: true, width: '25%' },
    { key: 'group', label: 'Account Group', sortable: true, width: '22%', render: (v) => <span className="badge badge-gray">{v || '-'}</span> },
    { key: 'balance', label: 'Balance', align: 'right', sortable: true, width: '15%', render: (v) => (
      <span className="font-medium" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatCurrency(v)}</span>
    )},
  ];

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Account Master</h1>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowImport(true)} className="btn btn-secondary flex items-center gap-2"><Upload size={16} /> Import</button>
          <button onClick={() => { setForm(emptyAccount); setEditing(null); setTouched({}); setSubmitted(false); setShowForm(true); }} className="btn btn-primary flex items-center gap-2">
            <Plus size={16} /> Add Account
          </button>
        </div>
      </div>

      <ImportModal
        open={showImport}
        onClose={() => setShowImport(false)}
        title="Import Accounts"
        dedupeKey="name"
        existing={state.accounts.map(a => a.name)}
        columns={[
          { key: 'name', label: 'Account Name', required: true },
          { key: 'group', label: 'Account Group' },
          { key: 'openingBalance', label: 'Opening Balance', type: 'number' },
          { key: 'balanceType', label: 'Balance Type' },
        ]}
        sample={{ name: 'HDFC Bank', group: 'Bank Accounts', openingBalance: 0, balanceType: 'Dr' }}
        onAdd={(p) => {
          const ob = Number(p.openingBalance) || 0;
          const bt = p.balanceType === 'Cr' ? 'Cr' : 'Dr';
          const g = (p.group || '').toLowerCase();
          const type = g.includes('bank') ? 'Bank' : g.includes('cash') ? 'Cash' : 'Other';
          dispatch({ type: 'ADD_ACCOUNT', payload: { ...emptyAccount, ...p, type, openingBalance: ob, balanceType: bt, balance: (bt === 'Cr' ? -1 : 1) * ob } });
        }}
      />

      <div className="card">
        <DataTable
          columns={columns}
          data={state.accounts}
          searchFields={['name', 'group']}
          emptyState={{ title: 'No accounts yet', description: 'Add your first account (Cash, Bank, etc.).', actionLabel: 'Add Account', onAction: () => { setForm(emptyAccount); setEditing(null); setTouched({}); setSubmitted(false); setShowForm(true); } }}
          actions={(row) => (
            <>
              <button onClick={() => handleEdit(row)} className="p-1.5 rounded hover:bg-blue-50 text-blue-600" title="Edit"><Edit2 size={15} /></button>
              <button onClick={() => handleDelete(row)} className="p-1.5 rounded hover:bg-red-50 text-red-600" title="Delete"><Trash2 size={15} /></button>
            </>
          )}
        />
      </div>

      <Modal isOpen={showForm} onClose={closeForm} title={editing ? 'Edit Account' : 'Add Account'}>
        <ErrorSummary errors={errors} show={submitted && hasErrors} />
        <div className="space-y-4">
          <FormField label="Account Name" required error={errors.name} touched={touched.name || submitted}>
            <input className="input" value={form.name} onChange={e => setForm({...form, name: e.target.value})} onBlur={() => touch('name')} autoFocus maxLength={100} placeholder="Enter account name" />
          </FormField>

          <FormField label="Account Group" required error={errors.group} touched={touched.group || submitted}
            hint={(state.accountGroups || []).length === 0 ? 'No account groups yet — add them in Masters → Account Group' : undefined}>
            <select className="input" value={form.groupId}
              onChange={e => {
                const g = (state.accountGroups || []).find(x => x.id === e.target.value);
                setForm({ ...form, groupId: e.target.value, group: g?.accountName || '' });
              }} onBlur={() => touch('group')}>
              <option value="">-- Select Account Group --</option>
              {(state.accountGroups || []).map(g => <option key={g.id} value={g.id}>{g.accountName}</option>)}
            </select>
          </FormField>

          <div className="grid grid-cols-2 gap-4">
            <FormField label="Opening Balance" error={errors.openingBalance} touched={touched.openingBalance || submitted} hint={editing ? 'Editing overrides the current balance' : undefined}>
              <input className="input text-right" type="number" min="0" step="0.01" value={form.openingBalance}
                onChange={e => setForm({...form, openingBalance: e.target.value})} onBlur={() => touch('openingBalance')} placeholder="0.00" />
            </FormField>

            <FormField label="Balance Type">
              <select className="input" value={form.balanceType} onChange={e => setForm({...form, balanceType: e.target.value})}>
                <option value="Dr">Debit (Dr)</option>
                <option value="Cr">Credit (Cr)</option>
              </select>
            </FormField>
          </div>
        </div>
        <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-100">
          <button onClick={closeForm} className="btn btn-secondary">Cancel</button>
          <button data-keyboard-save onClick={handleSave} className="btn btn-primary" disabled={submitted && hasErrors}>
            {editing ? 'Update' : 'Save'}
          </button>
        </div>
      </Modal>
    </div>
  );
}
